import React from 'react';
import TrekkoraIcon from '../images/TrekkoraIcon.png';
import SarahPersona from '../images/Sarah-persona.png';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400;1,500&family=DM+Sans:wght@300;400;500&display=swap');

  :root {
    --ink:       #1A1814;
    --ink-2:     #4A4740;
    --ink-3:     #8A8780;
    --rule:      rgba(26,24,20,0.10);
    --rule-md:   rgba(26,24,20,0.18);
    --sand:      #F7F4EE;
    --white:     #FFFFFF;
    --purple:    #3D2B7A;
    --purple-lt: #F0EEF8;
    --serif:     'Cormorant Garamond', Georgia, serif;
    --sans:      'DM Sans', system-ui, sans-serif;
  }

  /* ── HERO ─────────────────────────────────────────────────────────────────── */
  .tk-hero {
    background: var(--purple);
    padding: 80px 48px 72px;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 40px;
  }

  .tk-hero-left {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .tk-hero-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    object-fit: contain;
    background: rgba(255,255,255,0.1);
    padding: 6px;
    flex-shrink: 0;
  }

  .tk-hero-title {
    font-family: var(--serif);
    font-size: 56px;
    font-weight: 400;
    color: var(--white);
    line-height: 1;
  }

  .tk-hero-right {
    display: flex;
    gap: 32px;
    align-items: flex-end;
    padding-bottom: 4px;
  }

  .tk-hero-pill {
    font-family: var(--sans);
    font-size: 11px;
    font-weight: 400;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.5);
    border: 0.5px solid rgba(255,255,255,0.2);
    padding: 6px 14px;
    border-radius: 2px;
  }

  /* ── BODY ─────────────────────────────────────────────────────────────────── */
  .tk-body {
    max-width: 800px;
    margin: 0 auto;
    padding: 72px 48px 80px;
  }

  .tk-body p {
    font-family: var(--sans);
    font-size: 17px;
    font-weight: 400;
    line-height: 1.85;
    color: var(--ink);
    margin-bottom: 28px;
  }

  .tk-body p:last-child { margin-bottom: 0; }

  /* ── STAT CALLOUTS ────────────────────────────────────────────────────────── */
  .tk-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
    margin: 8px 0 36px;
    border-top: 0.5px solid var(--rule-md);
    border-bottom: 0.5px solid var(--rule-md);
  }

  .tk-stat {
    padding: 28px 32px;
    background: transparent;
  }

  .tk-stat:first-child {
    border-right: 0.5px solid var(--rule-md);
  }

  .tk-stat-number {
    font-family: var(--serif);
    font-size: 52px;
    font-weight: 400;
    color: var(--purple);
    line-height: 1;
    margin-bottom: 6px;
  }

  .tk-stat-desc {
    font-family: var(--sans);
    font-size: 13px;
    font-weight: 300;
    color: var(--ink-3);
    line-height: 1.5;
  }

  /* ── DIVIDER ──────────────────────────────────────────────────────────────── */
  .tk-divider {
    width: 100%;
    height: 0.5px;
    background: var(--rule-md);
    margin: 8px 0 36px;
  }

  /* ── PERSONA CARD ─────────────────────────────────────────────────────────── */
  .tk-persona {
    border: 0.5px solid var(--rule-md);
    border-radius: 4px;
    overflow: hidden;
    margin: 8px 0 36px;
  }

  .tk-persona-header {
    background: var(--purple);
    padding: 20px 28px;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .tk-persona-avatar {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(255,255,255,0.25);
    flex-shrink: 0;
  }

  .tk-persona-header-text {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .tk-persona-name {
    font-family: var(--serif);
    font-size: 26px;
    font-weight: 400;
    font-style: italic;
    color: var(--white);
  }

  .tk-persona-descriptor {
    font-family: var(--sans);
    font-size: 12px;
    font-weight: 300;
    color: rgba(255,255,255,0.55);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .tk-persona-body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    background: transparent;
  }

  .tk-persona-col {
    padding: 24px 28px;
  }

  .tk-persona-col:first-child {
    border-right: 0.5px solid var(--rule-md);
  }

  .tk-persona-col-label {
    font-family: var(--sans);
    font-size: 10px;
    font-weight: 400;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--ink-3);
    margin-bottom: 14px;
  }

  .tk-persona-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 7px 0;
    border-bottom: 0.5px solid var(--rule);
    font-family: var(--sans);
    font-size: 13px;
  }

  .tk-persona-row:last-child { border-bottom: none; }

  .tk-persona-row-label {
    font-weight: 300;
    color: var(--ink-3);
  }

  .tk-persona-row-value {
    font-weight: 400;
    color: var(--ink);
    text-align: right;
    max-width: 55%;
  }

  .tk-persona-trait {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 0;
    border-bottom: 0.5px solid var(--rule);
    font-family: var(--sans);
    font-size: 13px;
    font-weight: 300;
    color: var(--ink-2);
  }

  .tk-persona-trait:last-child { border-bottom: none; }

  .tk-persona-trait-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--purple);
    flex-shrink: 0;
    opacity: 0.5;
  }

  /* ── PROTOTYPE SECTION ────────────────────────────────────────────────────── */
  .tk-prototype {
    border-top: 0.5px solid var(--rule);
    padding: 64px 48px 80px;
    background: var(--sand);
  }

  .tk-prototype-header {
    max-width: 720px;
    margin: 0 auto 12px;
  }

  .tk-prototype-title {
    font-family: var(--serif);
    font-size: 28px;
    font-weight: 400;
    color: var(--ink);
    margin-bottom: 12px;
  }

  .tk-prototype-caveat {
    font-family: var(--sans);
    font-size: 13px;
    font-weight: 300;
    color: var(--ink-3);
    line-height: 1.7;
    max-width: 620px;
    margin-bottom: 36px;
  }

  .tk-prototype-note {
    font-family: var(--sans);
    font-size: 12px;
    font-weight: 300;
    color: var(--ink-3);
    margin-bottom: 16px;
    font-style: italic;
  }

  .tk-figma-wrap {
    max-width: 900px;
    margin: 0 auto;
    border-radius: 8px;
    overflow: hidden;
    border: 0.5px solid var(--rule-md);
    background: var(--white);
  }

  /* ── RESPONSIVE ──────────────────────────────────────────────────────────── */
  @media (max-width: 900px) {
    .tk-hero { padding: 56px 24px 48px; flex-direction: column; align-items: flex-start; }
    .tk-hero-title { font-size: 40px; }
    .tk-hero-right { flex-wrap: wrap; }
    .tk-body { padding: 48px 24px 64px; }
    .tk-stats { grid-template-columns: 1fr; }
    .tk-stat:first-child { border-right: none; border-bottom: 0.5px solid var(--rule-md); }
    .tk-prototype { padding: 48px 24px 64px; }
  }
`;

function TrekkoraPage() {
  return (
    <div>
      <style>{styles}</style>

      {/* ── HERO ── */}
      <div className="tk-hero">
        <div className="tk-hero-left">
          <img src={TrekkoraIcon} alt="Trekkora" className="tk-hero-icon" />
          <span className="tk-hero-title">Trekkora</span>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="tk-body">
        <p>Trekkora started as a two-week hackathon challenge, build a disruptive new business incorporating an LLM. I came in as the PM, responsible for defining what we'd build, running the research, and keeping us on track to hit a working prototype by the end. My co-founder handled the development side.</p>

        <p>We knew LLMs were getting good at synthesizing information quickly, and we wanted to find a space where that actually mattered to people. Travel planning stood out fast. Anyone who has planned a group trip knows how much time goes into it, tabs open across Google, TripAdvisor, Reddit threads, trying to piece together something that works for everyone. We thought an LLM could collapse that process significantly.</p>

        <p>Before writing a line of code I ran user research to test this assumption. I walked three people from our target demographic through planning a real trip using an LLM, acting as a kind of AI travel concierge. The technology handled vague, specific, and group-oriented requests surprisingly well but I wouldn't say it was perfect. One session involved planning a beach trip for seven friends on a budget in June. Within minutes we had options, tradeoffs, and a draft itinerary. The harder problems were real-time things like booking flights or checking hotel availability, which the LLM couldn't touch at the time.</p>

        <p>I also ran a survey of 15 people in the 22-30 range. A few things stood out.</p>

        <div className="tk-stats">
          <div className="tk-stat">
            <div className="tk-stat-number">93%</div>
            <div className="tk-stat-desc">were using search engines as their primary planning tool — established behavior, but generic tooling</div>
          </div>
          <div className="tk-stat">
            <div className="tk-stat-number">66%</div>
            <div className="tk-stat-desc">typically travel in groups of 3 or more — planning for yourself is a different problem than coordinating with other people</div>
          </div>
        </div>

        <p>The biggest pain point wasn't finding individual things, it was assembling them into a coherent itinerary that made logistical sense. The survey and interviews also helped sharpen who we were actually building for. Not just &ldquo;travelers&rdquo; but a pretty specific kind of person. We distilled that into &ldquo;Sarah&rdquo;, someone in their mid-to-late twenties who travels with friends a few times a year, tends to be the one who drives the planning, and finds the process more tedious than enjoyable.</p>

        <div className="tk-persona">
          <div className="tk-persona-header">
            <img src={SarahPersona} alt="Sarah" className="tk-persona-avatar" />
            <div className="tk-persona-header-text">
              <span className="tk-persona-name">Sarah</span>
              <span className="tk-persona-descriptor">Target user persona</span>
            </div>
          </div>
          <div className="tk-persona-body">
            <div className="tk-persona-col">
              <div className="tk-persona-col-label">Profile</div>
              <div className="tk-persona-row">
                <span className="tk-persona-row-label">Age</span>
                <span className="tk-persona-row-value">26</span>
              </div>
              <div className="tk-persona-row">
                <span className="tk-persona-row-label">Travel frequency</span>
                <span className="tk-persona-row-value">3+ domestic, 1 international per year</span>
              </div>
              <div className="tk-persona-row">
                <span className="tk-persona-row-label">Travels with</span>
                <span className="tk-persona-row-value">Friends, groups of 3+</span>
              </div>
              <div className="tk-persona-row">
                <span className="tk-persona-row-label">Planning role</span>
                <span className="tk-persona-row-value">Drives the plan, wants group input</span>
              </div>
              <div className="tk-persona-row">
                <span className="tk-persona-row-label">Tech comfort</span>
                <span className="tk-persona-row-value">Comfortable, not a power user</span>
              </div>
            </div>
            <div className="tk-persona-col">
              <div className="tk-persona-col-label">Behaviors</div>
              <div className="tk-persona-trait"><span className="tk-persona-trait-dot" />Organized and likes to plan ahead</div>
              <div className="tk-persona-trait"><span className="tk-persona-trait-dot" />Open to trying new tools</div>
              <div className="tk-persona-trait"><span className="tk-persona-trait-dot" />Doesn&apos;t enjoy the planning process itself</div>
              <div className="tk-persona-trait"><span className="tk-persona-trait-dot" />Wants the outcome, not the work</div>
              <div className="tk-persona-trait"><span className="tk-persona-trait-dot" />Comfortable with AI-assisted tools</div>
            </div>
          </div>
        </div>

        <p>Those findings helped guide us toward what we felt we should build.</p>

        <p>An itinerary-first experience where a user describes their trip and gets a shareable, editable plan back in minutes. Due to our limited time we kept the concept fairly simple. No booking integrations, no accounts, just fast itinerary generation that helped create a starting point. And we hit a functional prototype by the end of the two weeks!</p>

        <p>If we had kept going, the next step would have been driving real users to the prototype through social media to see if the concept was compelling enough to get clicks and real engagement. From there, hands-on testing with actual users to see how they interacted with it, what confused them, and what to build next. The core question we never got to answer was whether we actually solved the problem well enough to matter and bring users back.</p>
      </div>

      {/* ── PROTOTYPE ── */}
      <div className="tk-prototype">
        <div className="tk-prototype-header">
          <div className="tk-prototype-title">The prototype</div>
          <p className="tk-prototype-caveat">The prototype below was built separately from the hackathon MVP. A few months after the project wrapped I used Trekkora as a learning exercise when I was getting started with Figma. It's more polished than what we actually shipped in two weeks, and includes a few features we didn't get to, but the core concept is the same.</p>
          <p className="tk-prototype-note">Figma may prompt you to log in to view the prototype.</p>
        </div>
        <div className="tk-figma-wrap">
          <iframe
            style={{ border: 'none', display: 'block' }}
            width="100%"
            height="600"
            src="https://www.figma.com/embed?embed_host=share&url=https%3A%2F%2Fwww.figma.com%2Fproto%2FHVRF0o4fYsUiGBTarmhxVj%2FUntitled%3Fnode-id%3D5-28%26t%3DdYBaBCa4iBJymSaU-1%26scaling%3Dscale-down%26content-scaling%3Dfixed%26page-id%3D0%253A1%26starting-point-node-id%3D5%253A28"
            allowFullScreen
            title="Trekkora Figma Prototype"
          />
        </div>
      </div>
    </div>
  );
}

export default TrekkoraPage;
